/**
 * ProcurementGeo — interactive US tile map + dynamic filters + quick side
 * list for the Procurement Scanner dashboard. States are colored by total
 * open bid value ("money on the table"); hover shows counts; click filters
 * and opens the quick lead list. Includes federal/state toggle and
 * "save filter as alert" (Claire notifies on new matching bids).
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Loader2, BellPlus, ExternalLink, X, Landmark, Building2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard US tile-grid layout: [col, row] per state
const TILE = {
  AK: [0, 0], ME: [11, 0], VT: [10, 1], NH: [11, 1], WA: [1, 2], ID: [2, 2], MT: [3, 2], ND: [4, 2], MN: [5, 2], IL: [6, 2], WI: [5, 1], MI: [7, 1], NY: [9, 2], RI: [11, 2], MA: [10, 2],
  OR: [1, 3], NV: [2, 3], WY: [3, 3], SD: [4, 3], IA: [5, 3], IN: [6, 3], OH: [7, 3], PA: [8, 3], NJ: [9, 3], CT: [10, 3],
  CA: [1, 4], UT: [2, 4], CO: [3, 4], NE: [4, 4], MO: [5, 4], KY: [6, 4], WV: [7, 4], VA: [8, 4], MD: [9, 4], DE: [10, 4],
  AZ: [2, 5], NM: [3, 5], KS: [4, 5], AR: [5, 5], TN: [6, 5], NC: [7, 5], SC: [8, 5], DC: [9, 5],
  OK: [4, 6], LA: [5, 6], MS: [6, 6], AL: [7, 6], GA: [8, 6],
  HI: [0, 7], TX: [4, 7], FL: [9, 7],
};
const US_STATES = Object.keys(TILE).sort();

const fmtMoney = (v) => {
  if (!v) return "$0";
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

export default function ProcurementGeo({ isPt, trades = [] }) {
  const [geo, setGeo] = useState(null);
  const [trade, setTrade] = useState("");
  const [level, setLevel] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [hover, setHover] = useState(null);
  const [leads, setLeads] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);

  const loadGeo = useCallback(() => {
    axios.get(`${API}/public-works/geo-summary`, { params: { trade, level } })
      .then((r) => setGeo(r.data)).catch(() => {});
  }, [trade, level]);
  useEffect(() => { loadGeo(); }, [loadGeo]);

  useEffect(() => {
    if (!state) { setCities([]); setCity(""); return; }
    axios.get(`${API}/public-works/cities`, { params: { state } })
      .then((r) => setCities(r.data.cities || [])).catch(() => setCities([]));
  }, [state]);

  useEffect(() => {
    if (!state) { setLeads(null); return; }
    setLeadsLoading(true);
    const params = { state, limit: 20 };
    if (city) params.city = city;
    if (trade) params.trade = trade;
    if (level === "federal") params.source = "SAM.gov";
    axios.get(`${API}/public-works/opportunities`, { params })
      .then((r) => setLeads(r.data.items || r.data.opportunities || r.data || []))
      .catch(() => setLeads([]))
      .finally(() => setLeadsLoading(false));
  }, [state, city, trade, level]);

  const saveAlert = async () => {
    if (!state && !trade && !city) {
      toast.info(isPt ? "Selecione ao menos um filtro para salvar" : "Pick at least one filter"); return;
    }
    const name = [trade, city, state].filter(Boolean).join(" + ") || "Alerta Geo";
    try {
      await axios.post(`${API}/public-works/alerts`, {
        name, state, city, trade_category: trade, keyword: "", naics_code: "",
        source: level === "federal" ? "SAM.gov" : "", deadline_range_days: 30,
      });
      toast.success(isPt
        ? `🔔 Alerta "${name}" salvo — Claire avisará quando surgir um novo edital com esses critérios`
        : `🔔 Alert "${name}" saved — Claire will notify you on new matching bids`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const colorFor = (st) => {
    const d = geo?.states?.[st];
    if (!d) return "var(--al-border)";
    const ratio = geo.max_value > 0 ? d.total_value / geo.max_value
      : (geo.max_count > 0 ? d.count / geo.max_count : 0);
    const alpha = 0.18 + Math.min(ratio, 1) * 0.82;
    return `rgba(27, 58, 138, ${alpha.toFixed(2)})`;
  };

  const addToPipeline = async (opp) => {
    try {
      await axios.post(`${API}/public-works/pipeline`, { opportunity_id: opp.id, stage: "new" });
      toast.success(isPt ? "Adicionado ao Pipeline" : "Added to Pipeline");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="al-card p-5" data-testid="procurement-geo">
      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <MapPin className="w-4 h-4 text-[#1B3A8A]" />
        <h3 className="text-[13.5px] font-bold mr-2">{isPt ? "Mapa de Licitações — Dinheiro na Mesa" : "Bids Map — Money on the Table"}</h3>
        <select value={trade} onChange={(e) => setTrade(e.target.value)}
          data-testid="geo-filter-trade"
          className="h-8 px-2 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[11.5px]">
          <option value="">{isPt ? "Tipo de obra (todas)" : "Work type (all)"}</option>
          {trades.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={state} onChange={(e) => setState(e.target.value)}
          data-testid="geo-filter-state"
          className="h-8 px-2 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[11.5px]">
          <option value="">{isPt ? "Estado (todos)" : "State (all)"}</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={city} onChange={(e) => setCity(e.target.value)} disabled={!state}
          data-testid="geo-filter-city"
          className="h-8 px-2 rounded-lg border border-[var(--al-border)] bg-[var(--al-card-bg)] text-[11.5px] disabled:opacity-50">
          <option value="">{isPt ? "Cidade (todas)" : "City (all)"}</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        {/* Federal vs State toggle */}
        <div className="flex rounded-lg border border-[var(--al-border)] overflow-hidden" data-testid="geo-level-toggle">
          {[["", isPt ? "Todas" : "All", null], ["federal", "Federal", Landmark], ["state", isPt ? "Estadual" : "State/Local", Building2]].map(([v, lbl, Ic]) => (
            <button key={v} type="button" onClick={() => setLevel(v)}
              data-testid={`geo-level-${v || "all"}`}
              className={`h-8 px-2.5 text-[11px] font-semibold flex items-center gap-1 transition-colors ${
                level === v ? "bg-[#1B3A8A] text-white" : "text-[var(--al-text-muted)] hover:bg-black/5"}`}>
              {Ic && <Ic className="w-3 h-3" />}{lbl}
            </button>
          ))}
        </div>
        <button type="button" onClick={saveAlert}
          data-testid="geo-save-alert"
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#8B5CF6] text-white text-[11.5px] font-bold hover:bg-[#6D28D9]">
          <BellPlus className="w-3.5 h-3.5" />
          {isPt ? "Salvar filtro como Alerta" : "Save filter as Alert"}
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-5">
        {/* US tile map */}
        <div className="relative flex-1" data-testid="geo-map">
          <svg viewBox="0 0 396 264" className="w-full max-w-[560px]">
            {Object.entries(TILE).map(([st, [c, r]]) => {
              const d = geo?.states?.[st];
              const selected = state === st;
              return (
                <g key={st}
                  onMouseEnter={() => setHover(st)} onMouseLeave={() => setHover(null)}
                  onClick={() => setState(selected ? "" : st)}
                  style={{ cursor: "pointer" }} data-testid={`geo-state-${st}`}>
                  <rect x={c * 33 + 2} y={r * 33 + 2} width={29} height={29} rx={5}
                    fill={colorFor(st)}
                    stroke={selected ? "#C9A24E" : hover === st ? "#1B3A8A" : "transparent"}
                    strokeWidth={selected ? 3 : 2} />
                  <text x={c * 33 + 16.5} y={r * 33 + 18} textAnchor="middle" dominantBaseline="middle"
                    fontSize="10" fontWeight="700"
                    fill={d ? "#fff" : "var(--al-text-muted)"} pointerEvents="none">
                    {st}
                  </text>
                  {d?.count > 0 && (
                    <circle cx={c * 33 + 27} cy={r * 33 + 7} r={5.5} fill="#EF4444" />
                  )}
                  {d?.count > 0 && (
                    <text x={c * 33 + 27} y={r * 33 + 7.5} textAnchor="middle" dominantBaseline="middle"
                      fontSize="7" fontWeight="800" fill="#fff" pointerEvents="none">
                      {d.count > 99 ? "99" : d.count}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
          {hover && (
            <div className="absolute top-2 right-2 al-card px-3 py-2 shadow-lg text-[11.5px] pointer-events-none" data-testid="geo-tooltip">
              <div className="font-extrabold">{hover}</div>
              <div>{geo?.states?.[hover]?.count || 0} {isPt ? "leads ativos" : "active leads"}</div>
              <div className="font-bold text-[#1B3A8A]">{fmtMoney(geo?.states?.[hover]?.total_value)}</div>
            </div>
          )}
          <p className="text-[10px] text-[var(--al-text-muted)] mt-1">
            {isPt ? "Azul mais escuro = maior volume financeiro em licitações abertas. Clique num estado para filtrar."
              : "Darker blue = more open bid value. Click a state to filter."}
          </p>
        </div>

        {/* Quick side list */}
        {state && (
          <div className="w-full lg:w-[340px] shrink-0 border border-[var(--al-border)] rounded-xl overflow-hidden" data-testid="geo-side-list">
            <div className="px-4 py-2.5 bg-[#1B3A8A] text-white flex items-center gap-2">
              <span className="text-[12.5px] font-bold flex-1">
                {state}{city ? ` · ${city}` : ""} — {leads?.length ?? "..."} {isPt ? "leads" : "leads"}
              </span>
              <button type="button" onClick={() => setState("")} data-testid="geo-side-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[300px] overflow-y-auto divide-y divide-[var(--al-border)]">
              {leadsLoading && <div className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>}
              {!leadsLoading && (leads || []).length === 0 && (
                <p className="p-4 text-[11.5px] text-[var(--al-text-muted)]">
                  {isPt ? "Nenhum lead com esses filtros." : "No leads for these filters."}
                </p>
              )}
              {(leads || []).map((o) => (
                <div key={o.id} className="px-3.5 py-2.5 hover:bg-black/5" data-testid={`geo-lead-${o.id}`}>
                  <button type="button"
                    onClick={() => o.source_url && window.open(o.source_url, "_blank")}
                    className="text-left w-full">
                    <div className="text-[12px] font-semibold leading-snug line-clamp-2 hover:text-[#1B3A8A]">
                      {o.title} {o.source_url && <ExternalLink className="w-3 h-3 inline opacity-50" />}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 mt-1 text-[10.5px] text-[var(--al-text-muted)]">
                    <span className="truncate">{o.agency || o.source}</span>
                    {o.estimated_value ? <span className="font-bold text-[#16A34A]">{fmtMoney(o.estimated_value)}</span> : null}
                    <button type="button" onClick={() => addToPipeline(o)}
                      data-testid={`geo-lead-pipeline-${o.id}`}
                      className="ml-auto text-[10px] font-bold text-[#1B3A8A] hover:underline shrink-0">
                      + Pipeline
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
